import { find, isEqual, omit, sortBy } from 'lodash';
import { action, makeObservable, observable } from 'mobx';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';

import { EngineTab } from '@/constants';
import { ValidationMessage } from '@/types';

import { compressorGroupsEditableFields, EngineStepsEnum } from './constants';
import { convertNullToInfinity } from './utils';
import { DeviceMetadata } from '@/store/Devices/constants';

export class Compressors {
  @observable compressorsGroups: CompressorsGroup[];
  @observable oldValues: Omit<Compressors, 'oldValues'>;

  constructor(compressors?) {
    makeObservable(this);
    this.compressorsGroups =
      compressors?.compressorsGroups?.map(compressorGroup => new CompressorsGroup(compressorGroup)) ?? [];
    const compressorsGroups = window.localStorage.getItem(EngineStepsEnum.CompressorGroups);
    if (compressorsGroups) this.oldValues = JSON.parse(compressorsGroups, convertNullToInfinity);
  }

  @action addCompressorGroup() {
    this.compressorsGroups = [
      ...this.compressorsGroups,
      new CompressorsGroup({
        id: uuidv4(),
        name: `Group ${this.compressorsGroups?.length + 1}`,
        rotation: false,
        repeatEvery: 1,
        repeatOn: 'Sunday',
        time: moment({ hour: 14 }).format('h:mm a'),
      }),
    ];
  }

  @action setCompressorGroupField(compressorGroupId, field, value) {
    this.compressorsGroups = this.compressorsGroups?.map(compressorGroup =>
      compressorGroup?.id === compressorGroupId
        ? new CompressorsGroup({ ...compressorGroup, [field]: value })
        : compressorGroup,
    );
  }

  @action deleteCompressorGroup(compressorGroupId) {
    this.compressorsGroups = this.compressorsGroups?.filter(
      compressorGroup => compressorGroup?.id !== compressorGroupId,
    );
  }

  isCompressorInAnyGroup(compressorId: string): boolean {
    return this.compressorsGroups?.some(compressorGroup =>
      compressorGroup?.compressors?.some(compressor => compressor === compressorId),
    );
  }

  getCompressorGroupNameAndCompressorPriority(compressorId) {
    const compressorGroup = this.compressorsGroups.find(compressorGroup =>
      compressorGroup?.compressors?.includes(compressorId),
    );
    return {
      groupName: compressorGroup?.name,
      compressorPriority: compressorGroup?.compressors.indexOf(compressorId),
    };
  }

  validate(devicesMetadata: DeviceMetadata[], focusedField?) {
    let hasCompressorIsNotCompatibleToGroupError = false;
    return this.compressorsGroups?.reduce((result, current) => {
      const validationMessages: ValidationMessage[] = [];
      if (!current?.compressors?.length)
        validationMessages.push({
          key: current?.id,
          field: 'compressors',
          message: 'compressorsAreMandatory',
          focused: focusedField ? focusedField === `compressors-${current?.id}` : true,
          tab: EngineTab.CompressorGroups,
        });
      if (!+current?.repeatEvery)
        validationMessages.push({
          key: current?.id,
          field: 'repeatEvery',
          message: 'mustBeEqualOrHigherThan1',
          focused: focusedField ? focusedField === `repeatEvery-${current?.id}` : true,
          tab: EngineTab.CompressorGroups,
        });
      const currentCompressors: DeviceMetadata[] = (current?.compressors ?? []).map(id =>
        find(devicesMetadata, { id }),
      );
      this.validateHpEqualityInCompressors(currentCompressors, validationMessages, current, focusedField);
      current?.compressors?.forEach(compressorId => {
        const currentEditableFields = sortBy(find(devicesMetadata, { id: compressorId })?.editableFields ?? []);
        if (currentEditableFields.length) {
          const allCompressorsEditableFields = currentCompressors.map(deviceMetadata =>
            sortBy(deviceMetadata?.editableFields ?? []),
          );
          const isCompressorCompatibleToExistingCompressors = allCompressorsEditableFields.every(editableFields =>
            isEqual(currentEditableFields, editableFields),
          );
          if (!isCompressorCompatibleToExistingCompressors && !hasCompressorIsNotCompatibleToGroupError)
            validationMessages.push({
              key: current?.id,
              field: 'compressors',
              message: 'compressorIsNotCompatibleToGroup',
              focused: focusedField ? focusedField === `compressors-${current?.id}` : true,
              tab: EngineTab.CompressorGroups,
            });
          hasCompressorIsNotCompatibleToGroupError = true;
        }
      });
      return [...result, ...(validationMessages.length ? validationMessages : [])];
    }, []);
  }

  private validateHpEqualityInCompressors(
    currentCompressors: DeviceMetadata[],
    validationMessages: ValidationMessage[],
    current: CompressorsGroup,
    focusedField: unknown,
  ) {
    const hpValues = currentCompressors
      .filter(
        compressor =>
          compressor?.deviceModel &&
          compressor?.deviceModel['modelSpecs'] &&
          compressor?.deviceModel['modelSpecs']['hp'],
      )
      .map(compressor => compressor?.deviceModel['modelSpecs']['hp'])
      .filter(hp => hp !== undefined);
    if (hpValues.length && !hpValues.every(hp => hp === hpValues[0])) {
      validationMessages.push({
        key: current?.id,
        field: 'compressors',
        message: 'compressorsMustHaveSameHp',
        focused: focusedField ? focusedField === `compressors-${current?.id}` : true,
        tab: EngineTab.CompressorGroups,
      });
    }
  }

  hasChanges() {
    return (
      !!this.oldValues?.compressorsGroups?.length !== !!this.compressorsGroups?.length ||
      compressorGroupsEditableFields.some(field =>
        this.compressorsGroups?.some(
          compressorGroup =>
            !isEqual(
              compressorGroup?.[field],
              find(this.oldValues?.compressorsGroups, { id: compressorGroup.id })?.[field],
            ),
        ),
      )
    );
  }

  @action cancelChanges() {
    this.compressorsGroups = this.oldValues?.compressorsGroups ?? [];
  }

  getValuesToSave() {
    return omit(this, 'oldValues');
  }
}

export class CompressorsGroup {
  @observable id: string;
  @observable name: string;
  @observable rotation: boolean;
  @observable repeatEvery: number;
  @observable repeatOn: string;
  @observable time: typeof moment;
  @observable compressors: string[];

  constructor(compressorGroup?) {
    makeObservable(this);
    this.id = compressorGroup?.id;
    this.name = compressorGroup?.name;
    this.rotation = compressorGroup?.rotation;
    this.repeatEvery = compressorGroup?.repeatEvery;
    this.repeatOn = compressorGroup?.repeatOn;
    this.time = compressorGroup?.time;
    this.compressors = compressorGroup?.compressors;
  }
}
